import type { AuditPlanStatus, AuditPlanTeamRole, PublicUser, UserEntity, UserStatus } from '@/types/api'

/** First + last name when present, otherwise the email local part. */
export function displayName(user: Pick<PublicUser, 'firstName' | 'lastName' | 'email'>): string {
  const full = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
  if (full) return full
  return user.email.split('@')[0] ?? user.email
}

/** Up to two initials for an avatar fallback. */
export function initials(user: Pick<PublicUser, 'firstName' | 'lastName' | 'email'>): string {
  const first = user.firstName?.trim()?.[0]
  const last = user.lastName?.trim()?.[0]
  if (first && last) return `${first}${last}`.toUpperCase()
  if (first) return first.toUpperCase()
  return (user.email[0] ?? '?').toUpperCase()
}

const dateFormatter = new Intl.DateTimeFormat('en-PH', { dateStyle: 'medium' })
const dateTimeFormatter = new Intl.DateTimeFormat('en-PH', { dateStyle: 'medium', timeStyle: 'short' })

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? '—' : dateFormatter.format(parsed)
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? '—' : dateTimeFormatter.format(parsed)
}

/** Human labels for the backend enums. */
export const ENTITY_LABELS: Record<UserEntity, string> = {
  ADMIN: 'Administrator',
  STAFF: 'Staff',
}

export const STATUS_LABELS: Record<UserStatus, string> = {
  PENDING_EMAIL_VERIFICATION: 'Pending email',
  // Legacy ID-upload stage; no longer set for new accounts.
  PENDING_ID_SUBMISSION: 'Pending ID (legacy)',
  PENDING_REVIEW: 'Awaiting approval',
  PENDING_ACTIVATION: 'Pending activation',
  APPROVED: 'Active',
  REJECTED: 'Declined',
}

/** Human labels for the audit-plan lifecycle. */
export const AUDIT_PLAN_STATUS_LABELS: Record<AuditPlanStatus, string> = {
  DRAFT: 'Draft',
  SUBMITTED: 'Submitted',
  APPROVED: 'Approved',
  RETURNED: 'Returned',
  ARCHIVED: 'Archived',
}

/** Human labels for an audit-team member's role. */
export const AUDIT_PLAN_TEAM_ROLE_LABELS: Record<AuditPlanTeamRole, string> = {
  LEAD: 'Lead',
  MEMBER: 'Member',
  REVIEWER: 'Reviewer',
}

/** Format a Date/string as a `YYYY-MM-DD` value (the form's date storage format). */
export function toDateInputValue(value: string | Date | null | undefined): string {
  if (!value) return ''
  const parsed = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(parsed.getTime())) return ''
  return parsed.toISOString().slice(0, 10)
}
